<script lang="ts">
  /**
   * Persistent історія всіх bulk-import-прогонів (Settings → Імпорт даних
   * → Історія). Показує:
   *   - Список прогонів (хто, коли, скільки created/enriched/merged/failed)
   *   - Клік → drill-down: повний per-row лог з посиланнями на створені/
   *     збагачені Person'и
   */
  import { onMount } from 'svelte';
  import Icon from './Icon.svelte';
  import {
    fetchImportRuns,
    fetchImportRun,
    type ImportRunListItem,
    type ImportRunRow,
  } from '../lib/admin-api';
  import { fmtDateTime, fmtInt } from '../lib/format';

  let runs = $state<ImportRunListItem[]>([]);
  let loading = $state(true);
  let err = $state<string | null>(null);

  let selectedRun = $state<{ run: ImportRunListItem; rows: ImportRunRow[] } | null>(null);
  let loadingDetail = $state(false);

  // Фільтр по action для drill-down (created/enriched/merged/failed/all).
  let actionFilter = $state<'all' | 'created' | 'enriched' | 'merged' | 'failed'>('all');

  async function load() {
    loading = true;
    err = null;
    try {
      const r = await fetchImportRuns();
      runs = r.items;
    } catch (e) {
      err = e instanceof Error ? e.message : String(e);
    } finally {
      loading = false;
    }
  }

  async function openRun(id: string) {
    loadingDetail = true;
    selectedRun = null;
    actionFilter = 'all';
    try {
      selectedRun = await fetchImportRun(id);
    } catch (e) {
      err = e instanceof Error ? e.message : String(e);
    } finally {
      loadingDetail = false;
    }
  }

  onMount(load);

  function actionClass(a: string): string {
    if (a === 'created') return 'bg-emerald-100 text-emerald-700';
    if (a === 'enriched') return 'bg-blue-100 text-blue-700';
    if (a === 'merged') return 'bg-purple-100 text-purple-700';
    if (a === 'failed') return 'bg-rose-100 text-rose-700';
    return 'bg-slate-100 text-slate-500';
  }

  const filteredRows = $derived.by(() => {
    if (!selectedRun) return [];
    if (actionFilter === 'all') return selectedRun.rows;
    return selectedRun.rows.filter((r) => r.action === actionFilter);
  });

  function identifiersText(ids: Record<string, unknown>): string {
    const parts: string[] = [];
    if (ids['telegramId']) parts.push(`tg=${ids['telegramId']}`);
    if (ids['sendpulseContactId']) {
      const v = String(ids['sendpulseContactId']);
      parts.push(`sp=${v.length > 14 ? v.slice(0, 14) + '…' : v}`);
    }
    if (ids['email']) parts.push(`email=${ids['email']}`);
    if (ids['phone']) parts.push(`phone=${ids['phone']}`);
    return parts.join(' · ');
  }
</script>

{#if selectedRun}
  <!-- Drill-down view -->
  <div class="mb-3 flex items-center gap-2">
    <button
      class="text-[12px] text-[var(--color-pu-3)] hover:underline"
      onclick={() => (selectedRun = null)}
    >← До списку</button>
    <span class="text-[var(--color-text-muted-2)]">·</span>
    <span class="text-sm font-semibold">{fmtDateTime(selectedRun.run.startedAt)}</span>
    {#if selectedRun.run.actorEmail}
      <span class="text-[11px] text-[var(--color-text-muted)]">· {selectedRun.run.actorEmail}</span>
    {/if}
  </div>

  <div class="grid grid-cols-5 gap-2 mb-4">
    <div class="rounded-lg border border-[var(--color-border-3)] bg-white p-3">
      <div class="text-[10px] uppercase tracking-wider text-[var(--color-text-muted-2)] font-bold">Всього</div>
      <div class="text-base font-bold tabular-nums">{fmtInt(selectedRun.run.totalRows)}</div>
    </div>
    <div class="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
      <div class="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-emerald-700 font-bold"><Icon name="add" size={12} /> Створено</div>
      <div class="text-base font-bold text-emerald-800 tabular-nums">{fmtInt(selectedRun.run.createdCount)}</div>
    </div>
    <div class="rounded-lg border border-blue-200 bg-blue-50 p-3">
      <div class="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-blue-700 font-bold"><Icon name="refresh" size={12} /> Збагачено</div>
      <div class="text-base font-bold text-blue-800 tabular-nums">{fmtInt(selectedRun.run.enrichedCount)}</div>
    </div>
    <div class="rounded-lg border border-purple-200 bg-purple-50 p-3">
      <div class="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-purple-700 font-bold"><Icon name="url" size={12} /> Змерджено</div>
      <div class="text-base font-bold text-purple-800 tabular-nums">{fmtInt(selectedRun.run.mergedCount)}</div>
    </div>
    <div class="rounded-lg border border-rose-200 bg-rose-50 p-3">
      <div class="text-[10px] uppercase tracking-wider text-rose-700 font-bold">✗ Невалідних</div>
      <div class="text-base font-bold text-rose-800 tabular-nums">{fmtInt(selectedRun.run.invalidCount)}</div>
    </div>
  </div>

  <!-- Action filter pills -->
  <div class="flex items-center gap-1.5 mb-2">
    {#each ['all', 'created', 'enriched', 'merged', 'failed'] as a (a)}
      <button
        class="rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider {
          actionFilter === a
            ? 'bg-[var(--color-pu)] text-white'
            : 'bg-white border border-[var(--color-border-3)] text-[var(--color-text-muted)] hover:bg-[var(--color-bg-3)]'
        }"
        onclick={() => (actionFilter = a as typeof actionFilter)}
      >
        {a === 'all' ? 'Усі' : a}
      </button>
    {/each}
    <span class="ml-auto text-[10.5px] text-[var(--color-text-muted)]">
      Показано {filteredRows.length} з {selectedRun.rows.length}
    </span>
  </div>

  <div class="rounded-xl border border-[var(--color-border-3)] bg-white overflow-hidden">
    <table class="w-full text-xs">
      <thead class="bg-[var(--color-bg-3)] text-[10px] uppercase tracking-wider text-[var(--color-text-muted)]">
        <tr>
          <th class="text-left px-3 py-2 w-12">#</th>
          <th class="text-left px-3 py-2 w-24">Дія</th>
          <th class="text-left px-3 py-2">Identifiers</th>
          <th class="text-left px-3 py-2">Person</th>
          <th class="text-left px-3 py-2">Деталі</th>
        </tr>
      </thead>
      <tbody>
        {#each filteredRows as r (r.id)}
          <tr class="border-t border-[var(--color-border-3)] align-top hover:bg-[var(--color-bg-3)]">
            <td class="px-3 py-2 text-[var(--color-text-muted)] tabular-nums">{r.rowNumber}</td>
            <td class="px-3 py-2">
              <span class="rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider {actionClass(r.action)}">
                {r.action}
              </span>
            </td>
            <td class="px-3 py-2 font-mono text-[10px] text-slate-700 whitespace-nowrap">
              {identifiersText(r.identifiers as Record<string, unknown>)}
            </td>
            <td class="px-3 py-2 font-mono text-[10px]">
              {#if r.personId}
                <a href={`#/persons/${r.personId}`} class="text-[var(--color-pu-3)] hover:underline">
                  {r.personId.slice(0, 8)}…
                </a>
              {/if}
            </td>
            <td class="px-3 py-2 text-[10.5px]">
              {#if r.error}
                <span class="text-rose-700">{r.error}</span>
              {:else}
                {#if r.matchedBy.length > 0}
                  <div class="text-slate-500">match: {r.matchedBy.join(', ')}</div>
                {/if}
                {#if r.mergedLoserIds.length > 0}
                  <div class="text-purple-700">
                    merged {r.mergedLoserIds.length}: {r.mergedLoserIds.map((id) => id.slice(0, 8)).join(', ')}
                  </div>
                {/if}
                {#if r.message}
                  <div class="text-slate-600">{r.message}</div>
                {/if}
              {/if}
            </td>
          </tr>
        {/each}
        {#if filteredRows.length === 0}
          <tr><td colspan="5" class="px-3 py-6 text-center text-[var(--color-text-muted)] italic text-xs">Нема рядків з обраною дією</td></tr>
        {/if}
      </tbody>
    </table>
  </div>
{:else if loadingDetail}
  <div class="p-10 text-center text-sm text-[var(--color-text-muted)]">Завантаження…</div>
{:else}
  <!-- List view -->
  {#if loading}
    <div class="p-10 text-center text-sm text-[var(--color-text-muted)]">Завантаження…</div>
  {:else if err}
    <div class="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{err}</div>
  {:else if runs.length === 0}
    <div class="rounded-2xl border border-dashed border-[var(--color-border-3)] bg-white p-10 text-center">
      <div class="mb-2 inline-flex text-[var(--color-text-muted)]"><Icon name="folder" size={30} /></div>
      <div class="text-sm font-semibold mb-1">Ще не було жодного імпорту</div>
      <div class="text-xs text-[var(--color-text-muted)]">Перейди на вкладку "Імпорт" щоб залити перший файл.</div>
    </div>
  {:else}
    <div class="rounded-2xl border border-[var(--color-border-3)] bg-white overflow-hidden">
      <table class="w-full text-xs">
        <thead class="bg-[var(--color-bg-3)] text-[10px] uppercase tracking-wider text-[var(--color-text-muted)]">
          <tr>
            <th class="text-left px-4 py-2.5">Час</th>
            <th class="text-left px-4 py-2.5">Таблиця</th>
            <th class="text-left px-4 py-2.5">Користувач</th>
            <th class="text-right px-4 py-2.5">Всього</th>
            <th class="text-right px-4 py-2.5"><span class="inline-flex justify-end w-full"><Icon name="add" size={13} /></span></th>
            <th class="text-right px-4 py-2.5"><span class="inline-flex justify-end w-full"><Icon name="refresh" size={13} /></span></th>
            <th class="text-right px-4 py-2.5"><span class="inline-flex justify-end w-full"><Icon name="url" size={13} /></span></th>
            <th class="text-right px-4 py-2.5"><span class="inline-flex justify-end w-full"><Icon name="close" size={13} /></span></th>
            <th class="text-right px-4 py-2.5">Час, ms</th>
            <th class="w-8"></th>
          </tr>
        </thead>
        <tbody>
          {#each runs as r (r.id)}
            <tr
              class="border-t border-[var(--color-border-3)] hover:bg-[var(--color-bg-3)] cursor-pointer"
              onclick={() => openRun(r.id)}
            >
              <td class="px-4 py-2.5 whitespace-nowrap">{fmtDateTime(r.startedAt)}</td>
              <td class="px-4 py-2.5">
                <span class="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-700">
                  {r.tableSlug}
                </span>
              </td>
              <td class="px-4 py-2.5 text-[var(--color-text-muted)]">{r.actorEmail ?? '—'}</td>
              <td class="px-4 py-2.5 text-right tabular-nums">{fmtInt(r.totalRows)}</td>
              <td class="px-4 py-2.5 text-right tabular-nums text-emerald-700 font-semibold">{r.createdCount > 0 ? fmtInt(r.createdCount) : '—'}</td>
              <td class="px-4 py-2.5 text-right tabular-nums text-blue-700 font-semibold">{r.enrichedCount > 0 ? fmtInt(r.enrichedCount) : '—'}</td>
              <td class="px-4 py-2.5 text-right tabular-nums text-purple-700 font-semibold">{r.mergedCount > 0 ? fmtInt(r.mergedCount) : '—'}</td>
              <td class="px-4 py-2.5 text-right tabular-nums {r.invalidCount > 0 ? 'text-rose-700 font-semibold' : 'text-[var(--color-text-muted-2)]'}">{r.invalidCount > 0 ? fmtInt(r.invalidCount) : '—'}</td>
              <td class="px-4 py-2.5 text-right tabular-nums text-[var(--color-text-muted)]">{r.durationMs ?? '—'}</td>
              <td class="px-4 py-2.5 text-right text-xs text-[var(--color-text-muted)]">→</td>
            </tr>
          {/each}
        </tbody>
      </table>
    </div>
  {/if}
{/if}
